import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", subject: params.get("subject") || "", message: params.get("message") || "" });
  const [busy, setBusy] = useState(false);
  const enquiryType = params.get("type") || "";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", { ...form, enquiry_type: enquiryType });
      toast.success(data.message);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <Seo
        title="Contact Us"
        description="Get in touch with Hydronic Heating Controls and Valves. Call 07443 173458 or email enquiries@hhcv.co.uk — our heating specialists are here to help, Mon–Fri."
      />
      <span className="text-overline text-primary">We're here to help</span>
      <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Get in touch</h1>
      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10 mt-8">
        <div className="space-y-5">
          {[
            { i: Phone, t: "Call us", d: "07443 173458", s: "Mon–Fri, 8am–5pm" },
            { i: Mail, t: "Email", d: "enquiries@hhcv.co.uk", s: "We reply within 24 hours" },
            { i: MapPin, t: "Trading address", d: "Rear Heron Drive, Darlington", s: "Co. Durham, DL1 1DL, UK" },
          ].map((c) => (
            <div key={c.t} className="flex gap-4 bg-card border border-border rounded-sm p-5">
              <div className="shrink-0 grid place-items-center w-11 h-11 rounded-sm bg-primary/10 text-primary"><c.i className="w-5 h-5" /></div>
              <div><h3 className="font-semibold">{c.t}</h3><p className="text-sm">{c.d}</p><p className="text-xs text-muted-foreground">{c.s}</p></div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="bg-card border border-border rounded-sm p-6 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Name" v={form.name} on={set("name")} tid="contact-name" required />
            <F label="Email" type="email" v={form.email} on={set("email")} tid="contact-email" required />
          </div>
          <F label="Subject" v={form.subject} on={set("subject")} tid="contact-subject" required />
          <label className="block">
            <span className="text-sm font-medium">Message</span>
            <textarea value={form.message} onChange={set("message")} data-testid="contact-message" required rows={5}
              className="mt-1.5 w-full px-3 py-2.5 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
          </label>
          <button type="submit" disabled={busy} data-testid="contact-submit"
            className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors">
            <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send message"}
          </button>
          <p className="text-xs text-muted-foreground pt-1" data-testid="contact-junk-note">
            When we reply, please check your junk/spam folder in case our response lands there — and mark it as "Not junk" so future emails reach your inbox.
          </p>
        </form>
      </div>
    </div>
  );
}

function F({ label, v, on, type = "text", tid, required }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <input type={type} value={v} onChange={on} data-testid={tid} required={required}
        className="mt-1.5 w-full h-11 px-3 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
    </label>
  );
}
